<template>
  <!-- Cannot be button, focus-out on button does not work in Webkit/Safari-->
  <div aria-label="dropdown" tabindex="0" class="dropper" ref="dropper" :class="classMap" :style="styleMap" @click="toggleOpen" @keydown="handleKeydown" @focusout="reset">

    <!-- Start Input -->
    <div class="input backdrop">
      <!-- Display Value-->
      <div class="pills" v-if="value.length > 0">
        <span :class="{ pill: multiple }" v-for="v in value">
          <span>{{ valueName(v) }}</span>
          <span class="rm" v-if="multiple" @click.stop.prevent="removeItem(v)">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" data-slot="icon">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </span>
        </span>
      </div>
      <div class="pills" v-else>{{ defaultText }}</div>

      <!-- Right Side Icons -->
      <div class="icons">
        <span class="icon clear" @click.stop="clear" v-if="!multiple && clearable && value.length > 0">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" data-slot="icon">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
        </span>
        <span class="icon indicator">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" data-slot="icon">
            <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
          </svg>
        </span>
      </div>
      <!-- End Right Side Icons -->
    </div>
    <!--End Input-->

    <!-- List Options -->
    <ul class="backdrop" ref="list" role="listbox" aria-label="dropdown" @click.stop @mousemove="reset(false)">
      <li data-value="" v-if="clearable" @click="selectItem" role="option"><i>{{ defaultText }}</i></li>
      <li v-for="o in options" :key="o.value" @click="selectItem" :data-value="o.value" class="option" role="option">
        <span>{{ o.name }}</span>
        <span class="check" v-if="value.includes(o.value)">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" data-slot="icon">
            <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
        </span>
      </li>
    </ul>
    <!-- End List Options-->

  </div>
</template>

<script>
export default {
  name: "cms-select",
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      required: true,
    },
    defaultText: {
      type: String,
      default: "--select--"
    },
    clearable: {
      type: Boolean,
      default: true
    },
    multiple: {
      type: Boolean,
      default: false
    },
    listHeight: {
      type: Number
    },
  },
  data() {
    return {
      up: false, // Open list in up direction if no space at bottom of page
      open: false, // Is the list currently open
      keyboard: false, // Is the keyboard being used to navigate the list
      currentItem: null, // The currently selected item
    }
  },
  computed: {
    styleMap() {
      return {
        '--list-height': this.listHeight ? `${this.listHeight}px` : null
      }
    },
    classMap() {
      return {
        'up': this.up,
        'open': this.open,
        'keyboard': this.keyboard,
      }
    }
  },
  methods: {
    valueName(v) {
      return this.options.find(i => i.value == v)?.name
    },
    removeItem(v) {
      const has = this.value.findIndex(i => i == v)
      if (has > -1) {
        this.value.splice(has, 1)
      }
      this.$emit('input', this.value)
    },
    toggleOpen() {
      const combinedHeight = this.$refs.dropper.offsetTop + this.$refs.dropper.clientHeight + this.$refs.list.clientHeight;
      let wrap = document.querySelector(".filter-height-wrap");
      if (wrap) {
        this.up = combinedHeight > wrap.clientHeight
      }
      this.open = !this.open
    },
    selectItem(e) {
      // Item selected via click event
      if (e) {
        this.currentItem = e.currentTarget;
      }
      // Item selected with space/enter while hovereing over it
      if (!this.currentItem) {
        this.currentItem = this.$refs.list.querySelector("li:hover")
      }
      // We could not determine an active/selected item, so we just close
      if (!this.currentItem) {
        return this.reset()
      }

      const v = this.currentItem.dataset.value.trim();
      if (v == "") {
        return this.clear();
      }

      if (!this.multiple && !this.value.includes(v)) {
        this.$emit('input', [v])
        return this.reset();
      }

      if (this.multiple) {
        const has = this.value.findIndex(i => i == v)
        has > -1 ? this.value.splice(has, 1) : this.value.push(v)
        this.$emit('input', this.value)
        this.reset();
      }
    },
    reset(close = true) {
      this.currentItem?.classList.remove('hl')
      this.currentItem = null;
      this.keyboard = false;
      if (close) {
        this.open = false;
        this.$refs.list.scrollTo(0, 0);
      }
    },
    clear() {
      this.$emit('input', [])
      this.reset()
    },
    determineSelected() {
      if (!this.multiple && this.value.length > 0) {
        Array.from(this.$refs.list.children).forEach(c => {
          if (this.value.includes(c.dataset.value.trim())) {
            this.currentItem = c;
          }
        });
      } else {
        this.currentItem = this.$refs.list.firstChild
      }
      this.open = true;
      this.keyboard = true;
      this.$nextTick(() => {
        this.$refs.list.scroll({
          top: this.currentItem?.offsetTop || 0,
          behavior: "auto",
        });
      });
      this.currentItem?.classList.add('hl')
    },
    handleKeydown(e) {
      // If we are NOT tabbing, prevent the default event action
      if (e.keyCode != 9) {
        e.preventDefault();
      }
      // If we press escape, close the dropdown.
      if (e.keyCode == 27 && this.open) {
        return this.reset();
      }
      // If we press space or enter and the dropdown is open, select the item.
      if ([32, 13].includes(e.keyCode) && this.open) {
        return this.selectItem()
      }
      // If we press space, up or down, and the dropdown is not open, open it.
      if ([32, 38, 40].includes(e.keyCode) && !this.open) {
        return this.determineSelected()
      }
      // Handle selecting items with up and down keys
      if ([38, 40].includes(e.keyCode)) {
        const up = e.keyCode == 38 ? true : false
        let hoveredItem;
        if (!this.keyboard) {
          this.keyboard = true;
          hoveredItem = this.$refs.list.querySelector("li:hover")
          if (hoveredItem) {
            this.currentItem = up ? hoveredItem.previousElementSibling : hoveredItem.nextElementSibling
          } else {
            this.currentItem = this.$refs.list.firstChild
          }
          this.currentItem?.classList?.add('hl')
          return;
        }
        if (!this.currentItem) {
          this.currentItem = this.$refs.list.firstChild
        }
        if ((up && this.currentItem?.previousElementSibling) || (!up && this.currentItem?.nextElementSibling)) {
          this.currentItem?.classList?.remove('hl')
          this.currentItem = up ? this.currentItem.previousElementSibling : this.currentItem.nextElementSibling;
        }
        if (this.currentItem) {
          this.currentItem?.classList?.add('hl')
        }
        this.scrollToVisibleItem(up)
      }
    },
    scrollToVisibleItem(up) {
      let itemHeight = this.currentItem.offsetTop + this.currentItem.clientHeight;
      let diff = up ? (this.currentItem.offsetTop - this.$refs.list.scrollTop) : this.$refs.list.clientHeight - itemHeight;
      let move = (Math.abs(diff) > this.currentItem.clientHeight) ? this.currentItem.clientHeight : Math.abs(diff)
      if (diff < 0) {
        this.$refs.list.scrollBy({
          top: up ? (move * -1) : move,
          behavior: "smooth",
        });
      }
    },
  },
}
</script>

<style scoped>
.backdrop {
  margin: 0;
  font-size: 0.9rem;
  border-radius: 6px;
  outline: none;
  border: none;
  color: var(--crf-flip);
  background-color: var(--crf-bg-high);
  box-shadow: var(--crf-shadow);
}

div.dropper {
  width: 100%;
  display: inline-block;
  user-select: none;
  position: relative;
  outline: none;
  border: none;
  background: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
  border-radius: 6px;
  grid-template-rows: 40px 0;
  transition: grid-template-rows 250ms ease-in-out, opacity 10ms linear 125ms;
}

div.dropper:focus {
  outline: 1px solid var(--crf-accent);
}

div.input {
  padding: 8px 10px;
  width: 100%;
  max-width: 100%;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 0.9rem;
}

div.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: flex-start;
}

span.pill {
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  width: max-content;
  padding: 3px 7px;
  text-align: center;
  color: #fff;
  background-color: rgb(61, 61, 61);
  border: none;
  cursor: pointer;
  border-radius: 16px;
}

span.pill span.rm {
  border-radius: 50%;
  font-size: 9px;
  padding: 2px;
  display: inline-grid;
  place-items: center;
  width: 15px;
  height: 15px;
  aspect-ratio: 1;
  background-color: #959595;
}

div.icons {
  display: flex;
  align-items: center;
  gap: 4px;
}

span.icon {
  display: grid;
  place-content: center;
  padding: 2px;
  border-radius: 50%;
  font-weight: bold;
  background-color: var(--crf-accent);
  transition: transform 250ms ease-in-out;
}

span.icon.clear {
  background-color: rgb(61, 61, 61);
}

span.icon svg {
  width: 12px;
  height: 12px;
  aspect-ratio: 1;
  color: #fff;
}

ul {
  width: 100%;
  top: 45px;
  transform: scale(0);
  position: absolute;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: min(var(--list-height, 50vh), 50vh);
  overflow-y: auto;
  z-index: 999;
}

ul li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
}

span.check {
  display: grid;
  place-content: center;
  padding: 2px;
  border-radius: 50%;
  font-weight: bold;
  background-color: var(--crf-accent);
}

span.check svg {
  width: 10px;
  height: 10px;
  aspect-ratio: 1;
  color: #fff;
}

div.dropper ul li.hl,
div.dropper:not(.keyboard) ul li:hover {
  background-color: var(--crf-highlight);
}

div.dropper.open .icon.indicator {
  transform: rotate(180deg);
}

div.dropper.open ul {
  transform: scale(1);
  animation-name: slideInDown;
  animation-duration: 320ms;
  transform-origin: top center;
}

div.dropper.open.up ul {
  animation-name: none;
  transform: translateY(calc(-100% - 50px));
}


div.dropper[disabled] {
  cursor: not-allowed;
}

div[disabled] .backdrop {
  background-color: var(--crf-bg-mute);
  color: var(--crf-tx-dis);
}

div.dropper[disabled] span.icon {
  background-color: var(--crf-tx-dis);
}


@keyframes slideInDown {
  from {
    transform: translate3d(0, -5px, 0);
    visibility: visible;
  }

  to {
    transform: translate3d(0, 0, 0);
  }
}

@keyframes slideInUp {
  from {
    transform: translate3d(0, 5px, 0);
    visibility: visible;
  }

  to {
    transform: translate3d(0, 0, 0);
  }
}
</style>
